import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  AppShell,
  Avatar,
  Burger,
  Group,
  Loader,
  Menu,
  NavLink,
  Text,
  Title,
  ActionIcon,
  Alert,
  Button,
  UnstyledButton,
  useMantineColorScheme,
  Divider,
  Stack,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconPrompt,
  IconDashboard,
  IconSettings,
  IconSun,
  IconMoon,
  IconLogout,
  IconUser,
  IconAlertCircle,
} from "@tabler/icons-react";
import { useAuth } from "../contexts/AuthContext";
import { useWorkspace } from "../contexts/WorkspaceContext";
import { WorkspaceSwitcher } from "../components/WorkspaceSwitcher";
import { CreateWorkspaceModal } from "../components/CreateWorkspaceModal";

export function AppLayout() {
  const [opened, { toggle }] = useDisclosure();
  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure();
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { activeWorkspace, loading: wsLoading, error: wsError, refetchWorkspaces } = useWorkspace();

  const wsId = activeWorkspace?.id;

  const navItems = [
    {
      label: "Dashboard",
      icon: IconDashboard,
      path: wsId ? `/${wsId}` : "/",
    },
    {
      label: "Prompts",
      icon: IconPrompt,
      path: wsId ? `/${wsId}/prompts` : "/",
    },
  ];

  const displayName =
    user?.user_metadata?.display_name || user?.email?.split("@")[0] || "User";
  const initials = displayName
    .split(" ")
    .map((n: string) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  async function handleSignOut() {
    await signOut();
    navigate("/login");
  }

  return (
    <>
      <AppShell
        header={{ height: 56 }}
        navbar={{
          width: 260,
          breakpoint: "sm",
          collapsed: { mobile: !opened },
        }}
        padding="md"
      >
        <AppShell.Header>
          <Group h="100%" px="md" justify="space-between">
            <Group>
              <Burger
                opened={opened}
                onClick={toggle}
                hiddenFrom="sm"
                size="sm"
              />
              <Title order={3} fw={700}>
                Showrunner
              </Title>
            </Group>
            <Group gap="sm">
              <ActionIcon
                variant="transparent"
                size="lg"
                onClick={() => toggleColorScheme()}
                aria-label="Toggle color scheme"
              >
                {colorScheme === "dark" ? (
                  <IconSun size={18} />
                ) : (
                  <IconMoon size={18} />
                )}
              </ActionIcon>
              <Menu shadow="md" width={200} position="bottom-end">
                <Menu.Target>
                  <UnstyledButton aria-label="User menu">
                    <Avatar size="sm" radius="xl" color="blue">
                      {initials}
                    </Avatar>
                  </UnstyledButton>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>
                    {displayName}
                    <Text size="xs" c="dimmed">
                      {user?.email}
                    </Text>
                  </Menu.Label>
                  <Menu.Divider />
                  <Menu.Item
                    leftSection={<IconUser size={14} />}
                    onClick={() =>
                      navigate(wsId ? `/${wsId}/settings/profile` : "/")
                    }
                  >
                    Profile settings
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconSettings size={14} />}
                    onClick={() => navigate(wsId ? `/${wsId}/settings` : "/")}
                  >
                    Settings
                  </Menu.Item>
                  <Menu.Divider />
                  <Menu.Item
                    color="red"
                    leftSection={<IconLogout size={14} />}
                    onClick={handleSignOut}
                  >
                    Sign out
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Group>
        </AppShell.Header>

        <AppShell.Navbar p="sm">
          <AppShell.Section>
            <Group gap="sm" mb="md" px="xs">
              <Avatar size="md" radius="xl" color="blue">
                {initials}
              </Avatar>
              <div>
                <Text size="sm" fw={600} lh={1.2}>
                  {displayName}
                </Text>
                <Text size="xs" c="dimmed" lh={1.2}>
                  {user?.email}
                </Text>
              </div>
            </Group>
            <Divider mb="sm" />
          </AppShell.Section>

          <AppShell.Section>
            <Text size="xs" fw={500} c="dimmed" mb="xs" tt="uppercase">
              Workspace
            </Text>
            {wsLoading ? (
              <Group gap="xs" px="xs" py={6}>
                <Loader size="xs" />
                <Text size="sm" c="dimmed">Loading...</Text>
              </Group>
            ) : (
              <WorkspaceSwitcher onCreateClick={openCreate} />
            )}
            <Divider my="sm" />
          </AppShell.Section>

          <AppShell.Section grow>
            <Stack gap={2}>
              {navItems.map((item) => (
                <NavLink
                  key={item.label}
                  label={item.label}
                  leftSection={<item.icon size={18} stroke={1.5} />}
                  active={location.pathname === item.path}
                  onClick={() => {
                    navigate(item.path);
                    toggle();
                  }}
                  variant="light"
                  style={{ borderRadius: 6 }}
                />
              ))}
            </Stack>
          </AppShell.Section>

          <AppShell.Section>
            <Divider mb="sm" />
            <NavLink
              label="Workspace Settings"
              leftSection={<IconSettings size={18} stroke={1.5} />}
              active={location.pathname === `/${wsId}/settings/workspace`}
              onClick={() => {
                if (wsId) navigate(`/${wsId}/settings/workspace`);
              }}
              variant="light"
              style={{ borderRadius: 6 }}
            />
          </AppShell.Section>
        </AppShell.Navbar>

        <AppShell.Main>
          {wsError ? (
            <Alert
              icon={<IconAlertCircle size={16} />}
              title="Failed to load workspaces"
              color="red"
              variant="light"
              mb="md"
            >
              <Group gap="sm" align="center">
                <Text size="sm">{wsError}</Text>
                <Button size="xs" variant="light" onClick={refetchWorkspaces}>
                  Retry
                </Button>
              </Group>
            </Alert>
          ) : null}
          <Outlet />
        </AppShell.Main>
      </AppShell>

      <CreateWorkspaceModal opened={createOpened} onClose={closeCreate} />
    </>
  );
}
